import React, { useState } from 'react';
import { Crown, TrendingUp, TrendingDown } from 'lucide-react';
import type { HistoryEntry } from '../lib/api';

/** Cuántas partidas se enseñan de golpe; «Ver más» añade otras tantas. */
const PAGE = 10;

/**
 * Partidas de una cuenta: en qué trivia, en qué puesto y si abrió la sala.
 * La usan el perfil propio y el de cualquier otro usuario. Se enseñan de 10
 * en 10: el historial entero de golpe alargaba el perfil sin fin.
 */
export const ActivityList: React.FC<{ history: HistoryEntry[]; emptyText: string }> = ({
  history,
  emptyText,
}) => {
  const [visible, setVisible] = useState(PAGE);
  if (history.length === 0) return <p className="text-ink-500 text-sm">{emptyText}</p>;

  const remaining = history.length - visible;

  return (
    <>
    <ul className="divide-y divide-ink-800">
      {history.slice(0, visible).map((match, i) => {
        const host = match.was_host === 1;
        // Placement 0: dirigió la partida sin jugarla, así que no tiene puesto.
        const played = match.placement > 0;
        return (
          <li key={i} className="flex items-center gap-3 py-3">
            <span
              className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 ${
                !played
                  ? 'bg-sky-500/15 text-sky-300'
                  : match.placement === 1
                    ? 'bg-ember-500/20 text-ember-300'
                    : 'bg-ink-800 text-ink-400'
              }`}
              title={played ? `Puesto ${match.placement} de ${match.total_players}` : 'Anfitrión'}
            >
              {played ? `${match.placement}º` : <Crown className="w-4 h-4" />}
            </span>

            <div className="flex-1 min-w-0">
              <p className="text-ink-100 text-sm truncate font-medium">{match.quiz_title}</p>
              <p className="text-ink-500 text-xs flex items-center gap-1.5 flex-wrap">
                <span>{new Date(match.played_at).toLocaleDateString('es')}</span>
                <span>·</span>
                {played ? (
                  <span>
                    {match.placement}º de {match.total_players} · {Math.round(match.accuracy * 100)}%
                    acierto
                  </span>
                ) : (
                  <span>
                    {match.total_players} {match.total_players === 1 ? 'jugador' : 'jugadores'}
                  </span>
                )}
                <span
                  className={`chip text-[10px] py-0 ${
                    host
                      ? 'border-sky-700 bg-sky-500/10 text-sky-300'
                      : 'border-ink-700 bg-ink-850 text-ink-400'
                  }`}
                >
                  {host ? (played ? 'Anfitrión y jugador' : 'Anfitrión') : 'Jugador'}
                </span>
              </p>
            </div>

            {match.dahus_awarded > 0 && (
              <span className="chip border-ember-600 bg-ember-500/10 text-ember-300">
                +{match.dahus_awarded}
              </span>
            )}

            {match.rating_change !== 0 && (
              <span
                className={`flex items-center gap-0.5 text-sm font-mono shrink-0 ${
                  match.rating_change > 0 ? 'text-success-400' : 'text-danger-400'
                }`}
              >
                {match.rating_change > 0 ? (
                  <TrendingUp className="w-3.5 h-3.5" />
                ) : (
                  <TrendingDown className="w-3.5 h-3.5" />
                )}
                {match.rating_change > 0 ? '+' : ''}
                {match.rating_change}
              </span>
            )}
          </li>
        );
      })}
    </ul>
    {remaining > 0 && (
      <button
        onClick={() => setVisible((v) => v + PAGE)}
        className="mt-3 w-full py-2.5 rounded-lg text-sm font-semibold border border-ink-700 bg-ink-850
                   text-ink-200 hover:bg-ink-800 transition-colors"
      >
        Ver más · {remaining === 1 ? 'queda 1' : `quedan ${remaining}`}
      </button>
    )}
    </>
  );
};
